import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios'

import { env } from '#/shared/config/env'

import { ADMIN_AUTH_LOGIN_PATH, ADMIN_AUTH_PASSKEY_PATH, ADMIN_AUTH_RECOVERY_PATH } from './paths'

/**
 * The single axios instance for the admin API.
 *
 * One instance, not one per call site: axios keeps a keep-alive connection pool
 * per instance, so creating them ad hoc costs a fresh TCP+TLS handshake on every
 * request. Interceptors registered here therefore apply everywhere.
 */
export const httpClient: AxiosInstance = axios.create({
  baseURL: env.adminApiUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * The name the vendored product tree imports.
 *
 * Those files came over from the product verbatim and call it `serviceClient`
 * in a few thousand places; re-exporting keeps them byte-identical to their
 * source rather than carrying a rename that would have to be re-applied on
 * every re-vendor.
 */
export const serviceClient = httpClient

/** Called when the session is unrecoverable. Wired up by the auth module. */
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler
}

/**
 * Trades the stored refresh token for a new access token. Wired up by the auth
 * module, which owns where the refresh token lives.
 */
let refreshSession: (() => Promise<boolean>) | null = null

export function setSessionRefresher(refresher: () => Promise<boolean>): void {
  refreshSession = refresher
}

/**
 * The one refresh in flight, shared by every request that hits a 401 at once.
 *
 * A console page fans out to several services on load, so an expired token
 * produces a burst of simultaneous 401s. Each is a separate axios promise, and
 * refreshing per-promise would rotate the refresh token N times — the first
 * rotation invalidates the others' token and the burst signs the operator out.
 * Everyone waits on the same attempt instead.
 */
let inFlightRefresh: Promise<boolean> | null = null

function refreshOnce(): Promise<boolean> {
  if (!refreshSession) return Promise.resolve(false)
  inFlightRefresh ??= refreshSession().finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/** Whether a request would currently be sent authenticated. */
export function hasAccessToken(): boolean {
  return accessToken !== null
}

httpClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

/**
 * The calls that sign in, made while there is no session to refresh.
 *
 * Their 401 is the answer itself — a wrong password, a spent recovery code, a
 * setup that took too long — so it goes back to the form that asked instead of
 * starting a refresh that can only fail and bounce the page to /login.
 */
const SIGN_IN_PATHS = [ADMIN_AUTH_LOGIN_PATH, ADMIN_AUTH_RECOVERY_PATH, ADMIN_AUTH_PASSKEY_PATH]

function isRefreshCall(url: string | undefined): boolean {
  return url?.includes('/auth/refresh') ?? false
}

function isSignInCall(url: string | undefined): boolean {
  if (!url) return false
  return SIGN_IN_PATHS.some((path) => url === path || url.startsWith(`${path}/`))
}

/**
 * A 401 is an expired access token until proven otherwise.
 *
 * The access token lives 30 minutes and the refresh token seven days, so almost
 * every 401 an operator meets is the former running out mid-session. This tries
 * the refresh once and replays the request; only when the refresh itself fails
 * is the session actually over. Before this, the first 401 from any of eight
 * services cleared the token outright and the next navigation bounced to the
 * login screen — a signed-in operator was thrown out half an hour in, with a
 * valid refresh token sitting unused in storage.
 */
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined

    if (error.response?.status !== 401 || !config || isSignInCall(config.url)) {
      return Promise.reject(error)
    }

    if (isRefreshCall(config.url)) {
      accessToken = null
      onUnauthenticated?.()
      return Promise.reject(error)
    }

    // A 401 that survives a refresh is not an expired session. The token was
    // just reissued, so this endpoint is rejecting *this kind* of token — a
    // console screen pointed at a product route, say, which no admin token will
    // ever open. That is one widget's bug; ending the operator's session over
    // it is how a single stale call used to throw them back to the login form.
    if (config._retried) {
      return Promise.reject(error)
    }

    if (!(await refreshOnce())) {
      accessToken = null
      onUnauthenticated?.()
      return Promise.reject(error)
    }

    config._retried = true
    return httpClient.request(config)
  },
)

/**
 * Adapter for orval's generated clients.
 *
 * orval calls this for every operation, so the generated code stays free of any
 * knowledge about auth, base URLs, or error shape.
 */
export async function adminHttpClient<T>(config: AxiosRequestConfig): Promise<T> {
  const { data } = await httpClient.request<T>(config)
  return data
}

export default adminHttpClient

/**
 * `fetch` with the console's session, for responses axios cannot hand over as
 * they arrive — a streamed agent reply is read chunk by chunk from `body`.
 * Same token, same one refresh-and-replay on a 401 as the axios instance.
 */
export async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers)
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    return fetch(`${env.adminApiUrl}${path}`, { ...init, headers })
  }

  const response = await send()
  if (response.status !== 401) return response
  if (!(await refreshOnce())) {
    accessToken = null
    onUnauthenticated?.()
    return response
  }
  return send()
}
